import { State } from 'interfaces/state'
import { createSelector } from 'reselect'
import * as isNumber from 'lodash/isNumber'
import { PeergroupRelation, PeergroupTotals, PopupRelationId, RelationId } from 'interfaces/peergroups'
import { createOwnPropsSelector, lastMonthOfTimeframeSelector, memberInfoSelector } from 'selectors/globalSelectors'
import { CommonChartValues, Timeframe } from 'interfaces/chart'
import { TimeframedPageTyped, TotalledPage } from 'interfaces/pages'
import { TotalTyped } from 'interfaces/rank'
import { getTimeframeLastMonth } from 'helpers/dateTimeHelpers'
import { SLUGS } from 'constants/peergroups'

type PopupProps = {
  relationId: PopupRelationId
}

export const relationsSelector = (state: State) =>
  state.data.peergroups.relations

export const createRelationSelector = () => createSelector(
  relationsSelector,
  createOwnPropsSelector<{ relationId: RelationId }>(),
  (relations, { relationId }) => relations ? relations.byKey[relationId] : null,
)

export const peergroupsSelector = (state: State) =>
  state.data.peergroups.peergroups

export const createPeergroupByRelationSelector = () => createSelector(
  createRelationSelector(),
  peergroupsSelector,
  (relation, peergroups) =>
    relation
      ? (peergroups.byKey[relation.peergroupId] || null)
      : null,
)

const peergroupTotalsSelector = (state: State) =>
  state.data.peergroups.totals

export const peersRelationIdSelector = (state: State) =>
  state.data.peergroups.yourRelationId

export const peersRelationSelector = createSelector(
  peersRelationIdSelector,
  relationsSelector,
  (id, relations): PeergroupRelation | null => {
    if (!relations || !isNumber(id)) {
      return null
    }

    return relations.byKey[id] || null
  },
)

export const nationalAverageRelationIdSelector = createSelector(
  relationsSelector,
  (relations): RelationId | null => {
    return relations.allKeys.find(
      (id) =>
        relations.byKey[id].slug === SLUGS.everyone
    ) || null
  },
)

export const yourPeergroupIdSelector = (state: State) =>
  state.data.peergroups.yourPeergroupId

export const yourPeergroupSelector = createSelector(
  peergroupsSelector,
  yourPeergroupIdSelector,
  (peergroups, yourPeergroupId) => {
    if (!peergroups || !isNumber(yourPeergroupId)) {
      return null
    }

    return peergroups.byKey[yourPeergroupId] || null
  },
)

export const selectedRelationIdSelector = (state: State) =>
  state.app.sidebar.selectedRelationId

export const selectedPeergroupIdSelector = createSelector(
  relationsSelector,
  selectedRelationIdSelector,
  (relations, relationId): number | null =>
    isNumber(relationId) && relations && relations.byKey[relationId]
      ? relations.byKey[relationId].peergroupId
      : null,
)

export const selectedRelationSelector = createSelector(
  relationsSelector,
  selectedRelationIdSelector,
  (relations, id): PeergroupRelation | null =>
    isNumber(id) && relations && relations.byKey[id]
      ? relations.byKey[id]
      : null,
)

export const isPeergroupSelectedSelector = createSelector(
  selectedRelationIdSelector,
  createOwnPropsSelector<PopupProps>(),
  (selectedRelationId, { relationId }): boolean =>
    selectedRelationId === relationId,
)

export const isRandomizingSelector = (state: State) =>
  state.app.sidebar.isRandomizing

export const isPeergroupsLoadedSelector = createSelector(
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  (yourPeersId, selectedId) =>
    [yourPeersId, selectedId].every(isNumber),
)

export const shownPeergroupPopupIdSelector = (state: State) => {
  const { relationId } = state.app.sidebar.peergroupPopup
  return relationId || null
}

export const isPopupShownSelector = createSelector(
  shownPeergroupPopupIdSelector,
  createOwnPropsSelector<PopupProps>(),
  (shownId, { relationId }) => shownId === relationId,
)

export const anyPopupShownSelector = createSelector(
  shownPeergroupPopupIdSelector,
  Boolean,
)

export const peergroupPopupTimeoutIdSelector = (state: State): number | null => {
  const { timeoutId } = state.app.sidebar.peergroupPopup

  return timeoutId ? timeoutId : null
}

// ===========================================================================//
//                                   TOTALS                                   //
// ===========================================================================//

export const createSelectedTotalsSelector = (type: TotalledPage, timeframe: Timeframe) => createSelector(
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  peergroupTotalsSelector,
  (yourPeersId, selectedId, totals): PeergroupTotals | null => {
    const date = getTimeframeLastMonth(timeframe)

    if (!isNumber(yourPeersId) || !isNumber(selectedId)) {
      return null
    }

    return {
      peerValue: totals[date].byKey[yourPeersId][type],
      otherValue: totals[date].byKey[selectedId][type],
    }
  },
)

export const createSelectedTotalsByTypeSelector = () => createSelector(
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  peergroupTotalsSelector,
  lastMonthOfTimeframeSelector,
  createOwnPropsSelector<TimeframedPageTyped>(),
  (yourPeersId, selectedId, totals, date, { type }: TimeframedPageTyped): PeergroupTotals | null => {
    if (!isNumber(yourPeersId) || !isNumber(selectedId)) {
      return null
    }

    return {
      peerValue: totals[date].byKey[yourPeersId][type],
      otherValue: totals[date].byKey[selectedId][type],
    }
  },
)

export const hasSelectedTotalsSelector = createSelector(
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  peergroupTotalsSelector,
  lastMonthOfTimeframeSelector,
  (yourPeersId, selectedId, totals, date) =>
    Boolean(
      isNumber(yourPeersId) && isNumber(selectedId) &&
      totals &&
      totals[date] &&
      totals[date].byKey[yourPeersId] && totals[date].byKey[selectedId],
    ),
)

export const createAvgsByTypeSelector = () => createSelector(
  createSelectedTotalsByTypeSelector(),
  memberInfoSelector,
  createOwnPropsSelector<TotalTyped>(),
  (peerTotals, member, { type }): CommonChartValues | null => {
    if (!peerTotals || !member) {
      return null
    }
    const memberValue = member[type]
    if (!isNumber(memberValue)) {
      return null
    }

    return {
      memberValue,
      peerValue: peerTotals.peerValue.avg,
      otherValue: peerTotals.otherValue.avg,
    }
  },
)
